import { publicEnv } from '@/config/public-env.config.mjs';

/**
 * Response shape of Ahamove `GET /v1/order/city_detail?lat=&lng=`.
 *
 * Example:
 * ```json
 * {
 *   "_id": "DNI",
 *   "country_id": "VN",
 *   "name": "Dong Nai",
 *   "name_vi_vn": "thành phố Biên Hòa",
 *   "location": { "type": "Point", "coordinates": [107.806289, 10.204732] },
 *   "level": 2.0,
 *   "level_vn": "Tỉnh",
 *   "area_id": "75",
 *   "service_city_id": "DNI",
 *   "contract_number": 4173,
 *   "public_service": true
 * }
 * ```
 */
export interface CityDetailResponse {
  _id: string;
  country_id?: string;
  name?: string;
  name_vi_vn?: string;
  location?: {
    type: string;
    coordinates: [number, number];
  };
  level?: number;
  level_vn?: string;
  area_id?: string;
  service_city_id?: string;
  contract_number?: number;
  public_service?: boolean;
}

const CITY_DETAIL_PATH = '/v1/order/city_detail';

// Build the endpoint from the environment API base. Works on both the server
// (RSC redirects) and the client (location pickers) since it uses plain fetch.
const CITY_DETAIL_URL = (() => {
  const base = publicEnv.NEXT_PUBLIC_API_URI.replace(/\/+$/, '');
  return `${base}${CITY_DETAIL_PATH}`;
})();

// A single address change fans out into multiple resolvers (the location picker
// sets the store city_id, while its parent sheet resolves the same coords to
// decide navigation). These run concurrently with identical lat/lng, so we
// de-duplicate by coordinate:
//   - `inFlight` collapses concurrent calls into one network request.
//   - `cache` collapses rapid sequential lookups of the same coords (short TTL).
const CACHE_TTL_MS = 30_000;
const inFlight = new Map<string, Promise<CityDetailResponse | null>>();
const cache = new Map<string, { value: CityDetailResponse; expires: number }>();

const fetchCityDetail = async (
  lat: number,
  lng: number
): Promise<CityDetailResponse | null> => {
  try {
    const url = `${CITY_DETAIL_URL}?lat=${lat}&lng=${lng}`;
    const response = await fetch(url);

    if (!response.ok) return null;

    const data = (await response.json()) as CityDetailResponse;

    if (!data?._id) return null;

    return data;
  } catch (error) {
    console.error('Error in getCityDetailFn:', error);
    return null;
  }
};

/**
 * Resolve the city a coordinate belongs to via Ahamove's `city_detail` API.
 *
 * Replaces the legacy haversine/bounding-box zone detection. The returned
 * `_id` / `service_city_id` is the authoritative city_id.
 *
 * Concurrent/rapid calls for the same coordinates are de-duplicated (see the
 * caches above) so one address change triggers at most one network request.
 *
 * Returns `null` on invalid input or any network/parse error so callers can
 * apply their own fallback (city_id must always end up set). Errors are not
 * cached, so a later call can retry.
 */
export const getCityDetailFn = async (
  lat: number,
  lng: number
): Promise<CityDetailResponse | null> => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const key = `${lat},${lng}`;

  const cached = cache.get(key);
  if (cached && cached.expires > Date.now()) return cached.value;

  const pending = inFlight.get(key);
  if (pending) return pending;

  const promise = fetchCityDetail(lat, lng).finally(() => {
    inFlight.delete(key);
  });
  inFlight.set(key, promise);

  const result = await promise;

  // Only cache successful resolutions; let failures retry next time.
  if (result) {
    cache.set(key, { value: result, expires: Date.now() + CACHE_TTL_MS });
  }

  return result;
};
